import React, { useEffect, useState, useMemo } from 'react';
import { Modal, Row, Col, Select, Input, Button, message, Spin, Typography, Space, Tag } from 'antd';
import { useObjectsApi } from '../../../api/objects/objects';
import { useReferential } from '../../../context/ReferentialContext';
import { getImagePath } from '../../../utils/format/valueFormat';
import { CODIFICATION_TYPE } from '../../../constant/codification';
import { TProject } from '../../../models/project/project';
import { TImage } from '../../../models/objects/media';
import { useProjectApi } from '../../../api/projects/projects';

type Props = {
    editingProject: TProject | null;
    onClose: () => void;
    onSave: (project: TProject) => void;
};

const ProjectModal: React.FC<Props> = ({ editingProject, onClose, onSave }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [selectedImage, setSelectedImage] = useState<TImage | null>(null);
    const [creationType, setCreationType] = useState('');
    const [images, setImages] = useState<TImage[]>([]);
    const [isEligible, setIsEligible] = useState(false);
    const [eligibilityLoading, setEligibilityLoading] = useState(false);
    const [loading, setLoading] = useState(true);

    const palette = useMemo(
        () => ({
            primary: '#2D9CC9',
            secondary: '#8BBE1E',
            background: 'linear-gradient(135deg, rgba(45,156,201,0.08), rgba(139,190,30,0.06))',
            border: 'rgba(45,156,201,0.2)',
            muted: 'rgba(12, 24, 42, 0.68)',
        }),
        []
    );

    const { getImages } = useObjectsApi();
    const { getCodificationsByCodeType } = useReferential();
    const { createProject, updateProject, checkProjectEligibility } = useProjectApi();

    const codifications = getCodificationsByCodeType(CODIFICATION_TYPE.PROJECT_TYPE);

    useEffect(() => {
        setLoading(true);
        getImages('project')
            .then(res => setImages(res.data || []))
            .finally(() => setLoading(false));
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (editingProject) {
            setName(editingProject.name);
            setDescription(editingProject.description);
            setSelectedImage(editingProject.image || null);
            setCreationType(editingProject.project_type_id?.toString() || '');
            setIsEligible(true); 
        } else {
            setName('');
            setDescription('');
            setSelectedImage(null);
            setCreationType('');
            setIsEligible(false);
        }
        // eslint-disable-next-line
    }, [editingProject]);

    useEffect(() => {
        if (!editingProject && creationType) {
            const userId = 1;
            setEligibilityLoading(true);
            checkProjectEligibility(userId, parseInt(creationType))
                .then(res => setIsEligible(res.data?.eligible === true))
                .catch(() => setIsEligible(false))
                .finally(() => setEligibilityLoading(false));
        }
        // eslint-disable-next-line
    }, [creationType, editingProject]);

    const handleSubmit = async () => {
        if (!name || !description || !selectedImage || !creationType) {
            message.error('Veuillez remplir tous les champs.');
            return;
        }
        if (!editingProject && !isEligible) {
            message.error("Vous n'avez pas encore finalisé les leçons requises.");
            return;
        }

        try {
            if (editingProject) {
                const updated = {
                    ...editingProject,
                    name,
                    description,
                    image_id: selectedImage.id,
                    project_type_id: parseInt(creationType)
                };
                const res = await updateProject(editingProject.id, updated);
                onSave({ ...res.data, image: selectedImage });
                message.success('Projet modifié avec succès');
            } else {
                const res = await createProject({
                    id: 0,
                    name,
                    description,
                    image_id: selectedImage.id,
                    project_type_id: parseInt(creationType),
                    userapp_id: 0,
                    config: {}
                });
                onSave({ ...res.data, image: selectedImage });
                message.success('Projet créé avec succès');
            }
        } catch (err: any) {
            message.error(err.message || "Erreur lors de l'enregistrement du projet.");
        }
    };

    return (
        <Modal
            open
            onCancel={onClose}
            footer={null}
            width={960}
            styles={{
                content: {
                    borderRadius: 32,
                    overflow: 'hidden',
                    padding: 0,
                    border: `1px solid ${palette.border}`,
                    boxShadow: '0 24px 60px rgba(7,24,38,0.25)',
                },
                header: {
                    padding: '24px 32px 12px',
                    borderBottom: 'none',
                    background: palette.background,
                },
                body: {
                    padding: '16px 32px 32px',
                    background: '#f7fbff',
                },
            }}
            title={
                <Space direction="vertical" size={4}>
                    <Typography.Title level={3} style={{ margin: 0 }}>
                        {editingProject ? 'Modifier le projet' : 'Créer un projet'}
                    </Typography.Title>
                    <Typography.Text style={{ color: palette.muted }}>
                        Ajoute une image, choisis ton type puis raconte ton histoire.
                    </Typography.Text>
                </Space>
            }
        >
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
                    <Spin size="large" />
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                    <Row gutter={[24, 24]}>
                        <Col xs={24} md={12}>
                            <div
                                style={{
                                    borderRadius: 22,
                                    border: `1px solid ${palette.border}`,
                                    background: '#fff',
                                    boxShadow: '0 18px 36px rgba(7,24,38,0.12)',
                                    overflow: 'hidden',
                                    minHeight: 360,
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                {selectedImage ? (
                                    <>
                                        <img
                                            src={getImagePath(selectedImage.file_path)}
                                            alt={selectedImage.file_name}
                                            style={{
                                                width: '100%',
                                                height: 240,
                                                objectFit: 'cover',
                                            }}
                                        />
                                        <div style={{ padding: 16 }}>
                                            <Typography.Text style={{ color: palette.muted }}>
                                                {selectedImage.file_name}
                                            </Typography.Text>
                                        </div>
                                    </>
                                ) : (
                                    <div
                                        style={{
                                            flex: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: palette.muted,
                                            padding: 24,
                                            textAlign: 'center',
                                        }}
                                    >
                                        Aucune image sélectionnée
                                    </div>
                                )}
                            </div>
                        </Col>
                        <Col xs={24} md={12}>
                            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                <Select
                                    placeholder="Choisir une image"
                                    style={{ width: '100%' }}
                                    onChange={(val) =>
                                        setSelectedImage(images.find((img) => img.id === val) || null)
                                    }
                                    value={selectedImage?.id}
                                    optionLabelProp="label"
                                    size="large"
                                >
                                    {images.map((image) => (
                                        <Select.Option
                                            key={image.id}
                                            value={image.id}
                                            label={image.file_name}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <img
                                                    src={getImagePath(image.file_path)}
                                                    alt={image.file_name}
                                                    style={{
                                                        width: 48,
                                                        height: 48,
                                                        objectFit: 'cover',
                                                        borderRadius: 12,
                                                    }}
                                                />
                                                <span>{image.file_name}</span>
                                            </div>
                                        </Select.Option>
                                    ))}
                                </Select>
                                <Select
                                    placeholder="Type de projet"
                                    style={{ width: '100%' }}
                                    onChange={(val) => setCreationType(val)}
                                    value={creationType || undefined}
                                    size="large"
                                >
                                    {codifications.map((type) => (
                                        <Select.Option key={type.id} value={type.id.toString()}>
                                            {type.label}
                                        </Select.Option>
                                    ))}
                                </Select>
                                <Input
                                    placeholder="Nom du projet"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    size="large"
                                />
                                <Input.TextArea
                                    placeholder="Description"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    rows={6}
                                    style={{ resize: 'none' }}
                                />
                                {eligibilityLoading ? (
                                    <Spin size="small" />
                                ) : !editingProject && !isEligible ? (
                                    <Tag color="red">
                                        Tu dois terminer les leçons requises pour ce type.
                                    </Tag>
                                ) : null}
                            </Space>
                        </Col>
                    </Row>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 12,
                        }}
                    >
                        <Button onClick={onClose} size="large">
                            Annuler
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleSubmit}
                            size="large"
                            disabled={!editingProject && (!isEligible || eligibilityLoading)}
                            style={{
                                borderRadius: 999,
                                padding: '0 32px',
                                height: 48,
                                border: 'none',
                                background: `linear-gradient(135deg, ${palette.primary}, ${palette.secondary})`,
                                boxShadow: '0 16px 32px rgba(45,156,201,0.3)',
                                fontWeight: 700,
                                letterSpacing: '0.04em',
                            }}
                        >
                            {eligibilityLoading ? (
                                <Spin size="small" />
                            ) : editingProject ? (
                                'Enregistrer'
                            ) : (
                                'Commencer'
                            )}
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default ProjectModal;
